import { ExternalLink } from "lucide-react";
import FadeUp from "./FadeUp";

const items = [
    {
        name: "Kunle Campbell",
        role: "Investor · myco:soul",
        link: "https://apply.consciouscommerceco.com/",
        linkLabel: "consciouscommerceco.com",
        avatar:
            "https://customer-assets.emergentagent.com/job_uk-brand-seeding/artifacts/m7wvx8ln_Kunle.jpeg",
        quote: [
            "I had the pleasure of working with Ambuj during an influencer marketing campaign where he managed the influencer side on behalf of SARAL.",
            "What stood out most was his empathy, attention to detail, and the care he brought to the process. He took time to understand our needs, offered thoughtful guidance, and made sure the campaign moved forward with clarity.",
            "Ambuj went above and beyond throughout. He was patient, organised, and proactive. I also saw how he supported the people around him, which says a lot about his character and leadership.",
            "He is someone I would be happy to work with again. If I were building an influencer marketing function or team, Ambuj is exactly the kind of person I would want involved.",
        ],
    },
    {
        name: "Yash Chavan",
        role: "Founder & CEO · SARAL",
        link: "https://www.getsaral.com/",
        linkLabel: "getsaral.com",
        avatar:
            "https://customer-assets.emergentagent.com/job_uk-brand-seeding/artifacts/1r16m6oy_Yash.jpeg",
        quote: [
            "Ambuj worked with us for over 4 years and led our entire customer services department. He is amazing at building systems around influencer and affiliate programs, knows the right strategies to use based on constraints your brand may have, and executes everything from seeding to affiliate to paid partnerships.",
            "If you're looking for a team that can not just execute but own outcomes in your influencer program, look no further than Ambuj and his squad!",
        ],
    },
    {
        name: "Kevin Kirkpatrick",
        role: "CMO · Sunsama",
        link: "https://sunsama.com",
        linkLabel: "sunsama.com",
        avatar:
            "https://customer-assets.emergentagent.com/job_uk-brand-seeding/artifacts/b7r7fox2_kevin.png",
        quote: [
            "Working with Ambuj at Sunsama over the past two-plus years has been a game-changer for our influencer efforts. From day one, he took full ownership of the program: securing partnerships, negotiating terms, and crafting campaign strategies, without needing hand-holding.",
            "His deep understanding of influencer marketing trends and data-driven approach consistently grew our reach and engagement. Ambuj's reliability, initiative, and clear communication made collaboration effortless.",
            "I wholeheartedly recommend him to any company looking to scale their influencer program.",
        ],
    },
];

const Card = ({ t, i }) => (
    <FadeUp delay={i * 0.08}>
        <figure
            data-testid={`testimonial-card-${i}`}
            className="h-full bg-white rounded-3xl border border-black/[0.08] p-7 md:p-8 flex flex-col transition-all hover:border-black/20 hover:-translate-y-0.5"
        >
            <div className="flex items-start gap-4 pb-6 border-b border-black/[0.06]">
                <img
                    src={t.avatar}
                    alt={t.name}
                    loading="lazy"
                    className="h-14 w-14 md:h-16 md:w-16 rounded-full object-cover flex-none border border-black/5"
                />
                <div className="min-w-0 flex-1">
                    <div
                        data-testid={`testimonial-name-${i}`}
                        className="font-display font-bold text-lg tracking-tight text-[#0A0A0A] leading-[1.3]"
                    >
                        {t.name}
                    </div>
                    <div
                        data-testid={`testimonial-role-${i}`}
                        className="mt-1 text-[13px] text-gray-500 leading-[1.65]"
                    >
                        {t.role}
                    </div>
                    <a
                        href={t.link}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`testimonial-link-${i}`}
                        aria-label={`Visit ${t.linkLabel}`}
                        className="mt-2.5 inline-flex items-center gap-1.5 rounded-full border border-black/10 hover:border-[#0A0A0A]/40 hover:text-[#0A0A0A] transition-colors px-2.5 py-1 text-[11.5px] font-semibold text-gray-600 max-w-full"
                    >
                        <span className="truncate">{t.linkLabel}</span>
                        <ExternalLink className="h-3 w-3 flex-none" />
                    </a>
                </div>
            </div>

            <svg
                className="mt-6 h-5 w-5 text-[#0A0A0A]/40"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
            >
                <path d="M9 7H5a2 2 0 0 0-2 2v3a2 2 0 0 0 2 2h2v.5C7 16.99 5.99 18 4.5 18H4v2h.5C7.53 20 10 17.53 10 14.5V9a2 2 0 0 0-1-2zm11 0h-4a2 2 0 0 0-2 2v3a2 2 0 0 0 2 2h2v.5c0 1.49-1.01 2.5-2.5 2.5H15v2h.5c3.03 0 5.5-2.47 5.5-5.5V9a2 2 0 0 0-1-2z" />
            </svg>
            <blockquote className="mt-4 space-y-4 text-[14.5px] md:text-[15px] leading-[1.65] text-[#0A0A0A]/85 text-pretty flex-1">
                {t.quote.map((p, idx) => (
                    <p key={idx}>&ldquo;{p}&rdquo;</p>
                ))}
            </blockquote>
        </figure>
    </FadeUp>
);

export const Testimonials = () => (
    <section
        id="testimonials"
        data-testid="testimonials-section"
        className="py-24 md:py-32 bg-white"
    >
        <div className="container-page">
            <div className="max-w-3xl">
                <FadeUp>
                    <p className="text-xs font-bold tracking-[0.22em] uppercase text-gray-500">
                        Testimonials
                    </p>
                </FadeUp>
                <FadeUp delay={0.05}>
                    <h2
                        data-testid="testimonials-heading"
                        className="mt-4 font-display font-extrabold text-3xl md:text-4xl lg:text-5xl tracking-[-0.02em] leading-[1.3] text-[#0A0A0A] text-balance"
                    >
                        Built together.
                    </h2>
                </FadeUp>
            </div>

            <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-7 items-stretch">
                {items.map((t, i) => (
                    <Card key={t.name} t={t} i={i} />
                ))}
            </div>
        </div>
    </section>
);

export default Testimonials;
